import {CssBaseline} from '@mui/material';
import {configureApiUrl, resetApiClient, UserProvider} from '@smartmixin/core';
import {HelmetProvider} from 'react-helmet-async';
import {Provider as StoreProvider} from 'react-redux';
import {RouterProvider} from 'react-router';
import {PersistGate} from 'redux-persist/integration/react';
import {LanguageProvider, deviceLanguage} from './i18n';
import {SentryUtils} from './lib';
import {registerChunkReloadListener} from './lib/chunkReload';
import {initializeAnalytics} from './lib/initializeAnalytics';
import UserSyncProvider from './lib/UserSyncProvider';
import router from './navigation/router';
import store, {persistor} from './store';

// Apply persisted debug API URL after Redux rehydration
const applyDebugApiUrl = () => {
  const debugApiUrl = store.getState().user?.debugApiUrl;
  if (debugApiUrl) {
    configureApiUrl(debugApiUrl);
    resetApiClient();
  }
};
import {ThemeProvider} from './theme';

// API base URL:
//   - VITE_API_URL set: use it verbatim (E2E mock server, or an explicit
//     target). An empty value is meaningful -- it selects relative /api paths
//     -- so this tests for undefined rather than truthiness.
//   - dev server with nothing set: '' makes the client's baseURL '/api', a
//     same-origin path that the Vite dev server proxies to the real API (see
//     vite.config.ts). Same-origin requests are exempt from CORS, so the local
//     loop does not depend on the API allow-listing localhost.
//   - production build: fall through to the default in @smartmixin/core.
const apiUrl =
  import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? '' : undefined);
if (apiUrl !== undefined) {
  configureApiUrl(apiUrl);
}

initializeAnalytics(import.meta.env.VITE_GA_ID);

if (process.env.NODE_ENV !== 'development') {
  SentryUtils.configure();
}

// Self-heal stale-chunk failures after a deploy with a guarded reload
// (SMARTMIXIN-WEB-39); also handled at the router boundary in RouteErrorBoundary.
registerChunkReloadListener();

// Swap favicon for staging environment
if (import.meta.env.VITE_IS_STAGING === 'true') {
  document
    .querySelectorAll<HTMLLinkElement>('link[rel*="icon"]')
    .forEach((link) => {
      link.href = link.href.replace('favicon.png', 'favicon-staging.png');
      link.href = link.href.replace('favicon.ico', 'favicon-staging.png');
    });
}

const is24 =
  new Date(2014, 1, 1, 15, 0, 0, 0).toLocaleTimeString().indexOf('15') > -1;

export default function App() {
  return (
    <StoreProvider store={store}>
      <PersistGate
        loading={null}
        persistor={persistor}
        onBeforeLift={applyDebugApiUrl}>
        <UserProvider
          deviceLanguage={deviceLanguage}
          uses24HourClock={is24}
          usesMetricSystem={true}>
          <ThemeProvider>
            <CssBaseline />
            <LanguageProvider>
              <HelmetProvider>
                <UserSyncProvider>
                  <RouterProvider router={router} />
                </UserSyncProvider>
              </HelmetProvider>
            </LanguageProvider>
          </ThemeProvider>
        </UserProvider>
      </PersistGate>
    </StoreProvider>
  );
}
